// Tipos e regras do modal de Fornecedor (Suprimentos).
//
// Aqui mora tudo o que não é JSX: a forma do formulário, a leitura do que veio
// do banco e as duas regras que o formulário sozinho não consegue garantir
// (vigências que se cruzam e conversão de texto para número).

/** O contrato que o shell da Configuração já usa para chamar os modais novos. */
export interface SupplierModalProps {
  selectedObject: any;
  handleCloseModal: () => void;
  handleSave: (objectType: string, payload?: any) => any;
  parseInfoValue: (infoValue: string) => any;
  decryptedInfoToken?: any;
  /** `createSupplier` ou `updateSupplier`. O shell entrega; quem decide criar
   *  ou editar continua sendo o uuid, não esta string. */
  modalType?: string;
}

/**
 * Uma linha da tabela de Preço Acordado.
 *
 * Tudo é string porque vem de `<input>`: converter na digitação faz o campo
 * apagar o "0," de quem está no meio de digitar "0,5". A conversão acontece uma
 * vez só, na montagem do payload.
 *
 * `linhaId` NÃO é identificador de banco — é a chave local da lista, para o
 * React saber qual linha é qual antes de existir uuid nenhum.
 */
export interface PrecoAcordado {
  linhaId: string;
  skuUuid: string;
  unidadeCompra: string;
  preco: string;
  vigenciaInicio: string;
  vigenciaFim: string;
  quantidadeMinima: string;
  embalagem: string;
}

export interface FormularioDeFornecedor {
  nome: string;
  cnpj: string;
  contato: string;
  email: string;
  telefone: string;
  endereco: string;
  canalPreferido: string;
  prazoEntregaDias: string;
  condicoesPagamento: string;
  pedidoMinimoReais: string;
  freteTipo: string;
  freteValor: string;
  freteGratisAcima: string;
  janelaAtendimentoDias: string[];
  janelaAtendimentoInicio: string;
  janelaAtendimentoFim: string;
  diasDeEntrega: string[];
  janelaConsolidacaoHoras: string;
  precosAcordados: PrecoAcordado[];
}

export const CANAIS_PREFERIDOS = [
  { valor: "whatsapp", rotulo: "WhatsApp" },
  { valor: "email", rotulo: "E-mail" },
  { valor: "telefone", rotulo: "Telefone" },
] as const;

/**
 * Frete tem três formas e elas não convivem: "valor fixo" pede um R$, "grátis
 * acima de" pede outro R$ com significado oposto (piso de pedido, não custo).
 * Guardar os dois no mesmo campo é o defeito que já existe em produção na
 * coluna que mistura "3" com "10%".
 */
export const FRETE_TIPOS = [
  { valor: "nenhum", rotulo: "Sem frete" },
  { valor: "valorFixo", rotulo: "Valor fixo por entrega" },
  { valor: "gratisAcimaDe", rotulo: "Grátis acima de um valor" },
] as const;

export const DIAS_DA_SEMANA = [
  { valor: "seg", rotulo: "Seg" },
  { valor: "ter", rotulo: "Ter" },
  { valor: "qua", rotulo: "Qua" },
  { valor: "qui", rotulo: "Qui" },
  { valor: "sex", rotulo: "Sex" },
  { valor: "sab", rotulo: "Sáb" },
  { valor: "dom", rotulo: "Dom" },
] as const;

/**
 * Os quatro números que o fornecedor "tem" mas ninguém pode digitar: são
 * apurados no recebimento (o que chegou, quando chegou, por quanto). O módulo
 * de recebimento não existe. Digitar aqui seria inventar o placar do
 * fornecedor — por isso a tela mostra travessão e diz de quem depende.
 */
export const INDICADORES_CALCULADOS = [
  {
    chave: "leadTimeReal",
    rotulo: "Lead time real",
    depende:
      "média entre a data do pedido e a data de recebimento; depende do módulo de recebimento, que ainda não existe",
  },
  {
    chave: "entregueNoPrazo",
    rotulo: "Entregue no prazo (%)",
    depende:
      "recebimentos dentro do prazo prometido ÷ recebimentos; depende do módulo de recebimento, que ainda não existe",
  },
  {
    chave: "divergenciaDeQuantidade",
    rotulo: "Divergência de quantidade (%)",
    depende:
      "quantidade recebida contra a pedida; depende do módulo de recebimento, que ainda não existe",
  },
  {
    chave: "divergenciaDePreco",
    rotulo: "Divergência de preço (%)",
    depende:
      "preço da nota contra o preço acordado vigente; depende do módulo de recebimento, que ainda não existe",
  },
] as const;

export function formularioVazio(): FormularioDeFornecedor {
  return {
    nome: "",
    cnpj: "",
    contato: "",
    email: "",
    telefone: "",
    endereco: "",
    canalPreferido: "whatsapp",
    prazoEntregaDias: "",
    condicoesPagamento: "",
    pedidoMinimoReais: "",
    freteTipo: "nenhum",
    freteValor: "",
    freteGratisAcima: "",
    janelaAtendimentoDias: [],
    janelaAtendimentoInicio: "08:00",
    janelaAtendimentoFim: "18:00",
    diasDeEntrega: [],
    janelaConsolidacaoHoras: "",
    precosAcordados: [],
  };
}

export function linhaDePrecoVazia(): PrecoAcordado {
  return {
    linhaId: novaChaveDeLinha(),
    skuUuid: "",
    unidadeCompra: "",
    preco: "",
    vigenciaInicio: "",
    vigenciaFim: "",
    quantidadeMinima: "",
    embalagem: "",
  };
}

/**
 * Chave só de lista. `Date.now()` sozinho repete quando duas linhas nascem no
 * mesmo milissegundo (clicar duas vezes rápido), e chave repetida faz o React
 * embaralhar o que está sendo editado.
 */
let contadorDeLinhas = 0;
export function novaChaveDeLinha(): string {
  contadorDeLinhas += 1;
  return `linha-${Date.now()}-${contadorDeLinhas}`;
}

function texto(valor: any): string {
  return valor === null || valor === undefined ? "" : String(valor);
}

function lista(valor: any): string[] {
  return Array.isArray(valor) ? valor.map((item) => String(item)) : [];
}

/**
 * O JSON cru do fornecedor aberto.
 *
 * ARMADILHA: a linha que o shell entrega como `selectedObject` é a que a
 * DynamicTable monta no case 4, e ela NÃO carrega `supplierInfoValue` — só as
 * colunas já achatadas. Ler apenas `selectedObject.supplierInfoValue` devolvia
 * `{}` em toda edição, e aí canal, frete, janelas e a tabela inteira de preços
 * acordados voltavam vazios; salvar por cima apagaria o que estava gravado.
 *
 * Por isso a segunda fonte: procurar a linha crua dentro do próprio token, pelo
 * uuid, que é onde `supplierInfoValue` de fato mora.
 */
export function lerValorCruDoFornecedor(
  selectedObject: any,
  parseInfoValue: (infoValue: string) => any,
  decryptedInfoToken?: any
): any {
  if (!selectedObject) return {};

  if (selectedObject.supplierInfoValue) {
    return parseInfoValue(selectedObject.supplierInfoValue) || {};
  }

  const uuid =
    selectedObject.fornecedorUuid ??
    selectedObject.supplierUUID ??
    selectedObject.supplierInfoUuid;
  if (!uuid) return {};

  const linha = (decryptedInfoToken?.supplierInfo ?? []).filter(
    (info: any) => info?.supplierInfoUuid === uuid
  )[0];
  if (!linha?.supplierInfoValue) return {};

  return parseInfoValue(linha.supplierInfoValue) || {};
}

/**
 * Hidrata o formulário com o fornecedor aberto.
 *
 * Duas origens, nesta ordem: as colunas que a tabela de Fornecedores já monta
 * (`supplierName`, `supplierTaxPayerID`…, ver DynamicTable case 4) e o JSON
 * cru do fornecedor, que é onde os campos novos (canal, frete, janela) moram.
 * Nenhum dos dois é obrigatório: criar abre com `{}`.
 */
export function lerFornecedor(
  selectedObject: any,
  parseInfoValue: (infoValue: string) => any,
  decryptedInfoToken?: any
): FormularioDeFornecedor {
  const padrao = formularioVazio();
  if (!selectedObject) return padrao;

  const cru = lerValorCruDoFornecedor(
    selectedObject,
    parseInfoValue,
    decryptedInfoToken
  );

  return {
    nome: texto(
      selectedObject.supplierName ?? selectedObject.supplierInfoDescription
    ),
    cnpj: texto(selectedObject.supplierTaxPayerID ?? cru.supplierTaxPayerID),
    contato: texto(selectedObject.supplierContact ?? cru.supplierContact),
    email: texto(selectedObject.supplierEmail ?? cru.supplierEmail),
    telefone: texto(selectedObject.supplierPhone ?? cru.supplierPhone),
    endereco: texto(selectedObject.supplierAddress ?? cru.supplierAddress),
    canalPreferido: texto(cru.canalPreferido) || padrao.canalPreferido,
    prazoEntregaDias: texto(
      selectedObject.supplierDeliveryTime ?? cru.prazoEntregaDias
    ),
    condicoesPagamento: texto(
      selectedObject.supplierPaymentTerms ?? cru.condicoesPagamento
    ),
    pedidoMinimoReais: texto(cru.pedidoMinimoReais),
    freteTipo: texto(cru.freteTipo) || padrao.freteTipo,
    freteValor: texto(cru.freteValor),
    freteGratisAcima: texto(cru.freteGratisAcima),
    janelaAtendimentoDias: lista(cru.janelaAtendimentoDias),
    janelaAtendimentoInicio:
      texto(cru.janelaAtendimentoInicio) || padrao.janelaAtendimentoInicio,
    janelaAtendimentoFim:
      texto(cru.janelaAtendimentoFim) || padrao.janelaAtendimentoFim,
    diasDeEntrega: lista(cru.diasDeEntrega),
    janelaConsolidacaoHoras: texto(cru.janelaConsolidacaoHoras),
    precosAcordados: (Array.isArray(cru.precosAcordados)
      ? cru.precosAcordados
      : []
    ).map((preco: any) => ({
      linhaId: novaChaveDeLinha(),
      skuUuid: texto(preco?.skuUuid),
      unidadeCompra: texto(preco?.unidadeCompra),
      preco: texto(preco?.preco),
      vigenciaInicio: texto(preco?.vigenciaInicio),
      vigenciaFim: texto(preco?.vigenciaFim),
      quantidadeMinima: texto(preco?.quantidadeMinima),
      embalagem: texto(preco?.embalagem),
    })),
  };
}

/** Como a vigência aparece para o usuário no aviso de sobreposição. */
export function descreverVigencia(preco: PrecoAcordado): string {
  const inicio = preco.vigenciaInicio || "(sem início)";
  const fim = preco.vigenciaFim || "sem fim";
  return `${inicio} até ${fim}`;
}

export interface Sobreposicao {
  skuUuid: string;
  primeira: PrecoAcordado;
  segunda: PrecoAcordado;
}

/**
 * Duas vigências do MESMO sku não podem se cruzar.
 *
 * Se cruzarem, existem dois preços válidos no mesmo dia e nada no sistema
 * decide qual vale — o pedido sairia com o preço que o `find` encontrasse
 * primeiro, que é a ordem de digitação. Vigência sem fim é aberta: cobre até o
 * infinito, então qualquer coisa que comece depois dela colide.
 *
 * As datas vêm de `<input type="date">`, sempre no formato AAAA-MM-DD, em que
 * comparação de texto é comparação de calendário — sem `new Date`, que
 * reinterpretaria a data como UTC e derrubaria o dia em fuso negativo.
 */
export function acharSobreposicao(
  precos: PrecoAcordado[]
): Sobreposicao | null {
  for (let i = 0; i < precos.length; i += 1) {
    for (let j = i + 1; j < precos.length; j += 1) {
      const a = precos[i];
      const b = precos[j];
      if (!a.skuUuid || a.skuUuid !== b.skuUuid) continue;
      if (!a.vigenciaInicio || !b.vigenciaInicio) continue;

      const fimA = a.vigenciaFim || "9999-12-31";
      const fimB = b.vigenciaFim || "9999-12-31";
      if (a.vigenciaInicio <= fimB && b.vigenciaInicio <= fimA) {
        return { skuUuid: a.skuUuid, primeira: a, segunda: b };
      }
    }
  }
  return null;
}

/**
 * Texto para número, na hora de montar o payload.
 *
 * Campo vazio vira `null`, não `0`: "não informei o pedido mínimo" e "o pedido
 * mínimo é zero" são coisas diferentes, e `Number("")` responde 0 às duas.
 */
export function numeroOuNulo(valor: string): number | null {
  const limpo = (valor ?? "").trim();
  if (limpo === "") return null;
  const numero = Number(limpo);
  return Number.isFinite(numero) ? numero : null;
}
